<script setup lang="ts">
import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { apiClient } from '@/lib/api';
import BaseButton from '@/components/BaseButton.vue';
import { usePayment } from '@/composables/firebase/payment';

const toast = useToast();
const { t } = useI18n();

const {
  // @ts-ignore
  buttonIcon = 'pen-to-square' as IconProp,
  buttonLabel = null,
  disabled = false,
} = defineProps<{
  buttonIcon?: IconProp;
  buttonLabel?: string;
  disabled?: boolean;
}>();

const { outstandingPayment, subscriptionStatus } = usePayment();

const isLoading = ref(false);

async function changePaymentMethod() {
  const { data, error } = await apiClient.consultant.payment.mandate.create.post();

  if (error) {
    toast.error(
      typeof (error.value as any).message === 'string'
        ? (error.value as any).message
        : t('generic.errors.generic'),
    );
  } else if (data?.redirect) {
    location.href = data.redirect;
  }
}

async function payOutstandingPayment() {
  if (outstandingPayment.value) {
    const { data, error } = await apiClient.consultant.payment.outstanding.pay.post({
      paymentId: outstandingPayment.value.id,
    });

    if (error) {
      toast.error(
        typeof (error.value as any).message === 'string'
          ? (error.value as any).message
          : t('generic.errors.generic'),
      );
    } else if (data?.redirect) {
      location.href = data.redirect;
    }
  }
}

async function startPaymentMethodChangeFlow() {
  isLoading.value = true;
  if (outstandingPayment.value) {
    await payOutstandingPayment();
  } else {
    await changePaymentMethod();
  }
}
</script>

<template>
  <div>
    <div class="flex items-center">
      <BaseButton
        class="btn-primary"
        :icon="buttonIcon"
        :disabled="disabled || isLoading"
        :loading="isLoading"
        @click="startPaymentMethodChangeFlow">
        <template v-if="buttonLabel">
          {{ buttonLabel }}
        </template>
        <template v-else>
          {{ $t('userProfile.sections.paymentMethod.changeCta') }}
        </template>
      </BaseButton>

      <div
        v-if="!buttonLabel && outstandingPayment && subscriptionStatus === 'suspended'"
        class="ml-2 text-s text-danger">
        {{ $t('userProfile.sections.paymentMethod.suspendedHint') }}
      </div>
    </div>
  </div>
</template>
